import React, { useMemo, useState } from 'react';

export interface VaultFileNode {
  name: string;
  path: string;
  type: 'file';
  title: string;
}
export interface VaultFolderNode {
  name: string;
  path: string;
  type: 'folder';
  children: VaultTreeNode[];
}
export type VaultTreeNode = VaultFileNode | VaultFolderNode;

interface Props {
  nodes: VaultTreeNode[];
  selectedPath: string | null;
  onSelect: (path: string) => void;
  /** Folder paths that should start expanded on first render. */
  initialExpanded?: string[];
}

export default function VaultTree({ nodes, selectedPath, onSelect, initialExpanded = [] }: Props) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(initialExpanded));

  const q = query.trim().toLowerCase();

  // Filter tree by search query. When filtering, folders that have any matching
  // descendant stay; otherwise everything is dropped. Matching files are kept.
  const filtered = useMemo(() => (q ? filterTree(nodes, q) : nodes), [nodes, q]);

  // When searching, auto-expand everything in the filtered set.
  const forceExpanded = q.length > 0;

  function isExpanded(p: string): boolean {
    if (forceExpanded) return true;
    return expanded.has(p);
  }

  function toggle(p: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  }

  const fileCount = countFiles({ type: 'folder', name: '', path: '', children: filtered });

  return (
    <div className="vault-tree-wrapper">
      <div className="vault-tree-search">
        <span className="vault-tree-search__icon" aria-hidden>{'\u{1F50D}'}</span>
        <input
          className="vault-tree-search__input"
          type="text"
          placeholder="Filter notes..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          spellCheck={false}
        />
        {query && (
          <button
            className="vault-tree-search__clear"
            onClick={() => setQuery('')}
            aria-label="Clear filter"
            type="button"
          >
            {'\u2715'}
          </button>
        )}
      </div>
      <div className="vault-tree-meta">
        <span>{fileCount} {fileCount === 1 ? 'note' : 'notes'}</span>
        {q && <span className="vault-tree-meta__filter">filtered</span>}
      </div>
      <ul className="vault-tree">
        {filtered.length === 0 && (
          <li className="vault-tree__empty">No matching notes.</li>
        )}
        {filtered.map((node) => (
          <TreeNode
            key={node.path}
            node={node}
            selectedPath={selectedPath}
            onSelect={onSelect}
            isExpanded={isExpanded}
            toggle={toggle}
            depth={0}
            query={q}
          />
        ))}
      </ul>
    </div>
  );
}

function TreeNode({
  node,
  selectedPath,
  onSelect,
  isExpanded,
  toggle,
  depth,
  query,
}: {
  node: VaultTreeNode;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  isExpanded: (p: string) => boolean;
  toggle: (p: string) => void;
  depth: number;
  query: string;
}) {
  if (node.type === 'folder') {
    const expanded = isExpanded(node.path);
    const count = countFiles(node);
    return (
      <li className="vault-tree__folder">
        <button
          className="vault-tree__folder-btn"
          onClick={() => toggle(node.path)}
          style={{ paddingLeft: `${depth * 12 + 4}px` }}
          type="button"
        >
          <span className={`vault-tree__chevron${expanded ? ' vault-tree__chevron--open' : ''}`}>
            {'\u25B8'}
          </span>
          <span className="vault-tree__folder-icon" aria-hidden>
            {expanded ? '\u{1F4C2}' : '\u{1F4C1}'}
          </span>
          <span className="vault-tree__folder-name">{node.name}</span>
          <span className="vault-tree__count">{count}</span>
        </button>
        {expanded && node.children.length > 0 && (
          <ul className="vault-tree__children">
            {node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                selectedPath={selectedPath}
                onSelect={onSelect}
                isExpanded={isExpanded}
                toggle={toggle}
                depth={depth + 1}
                query={query}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  const isSelected = selectedPath === node.path;
  return (
    <li className="vault-tree__file">
      <button
        className={`vault-tree__file-btn${isSelected ? ' vault-tree__file-btn--active' : ''}`}
        onClick={() => onSelect(node.path)}
        style={{ paddingLeft: `${depth * 12 + 28}px` }}
        title={node.path}
        type="button"
      >
        <span className="vault-tree__file-icon" aria-hidden>{'\u{1F4C4}'}</span>
        <span className="vault-tree__file-name">
          {query ? highlight(node.title, query) : node.title}
        </span>
      </button>
    </li>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function countFiles(node: VaultTreeNode): number {
  if (node.type === 'file') return 1;
  return node.children.reduce((sum, c) => sum + countFiles(c), 0);
}

function filterTree(nodes: VaultTreeNode[], q: string): VaultTreeNode[] {
  const out: VaultTreeNode[] = [];
  for (const node of nodes) {
    if (node.type === 'file') {
      if (node.title.toLowerCase().includes(q) || node.path.toLowerCase().includes(q)) {
        out.push(node);
      }
    } else {
      const children = filterTree(node.children, q);
      // Also include the folder if its own name matches
      if (children.length > 0 || node.name.toLowerCase().includes(q)) {
        out.push({ ...node, children });
      }
    }
  }
  return out;
}

function highlight(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return text;
  const end = idx + q.length;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="vault-tree__hl">{text.slice(idx, end)}</mark>
      {text.slice(end)}
    </>
  );
}
